import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Clock, 
  Star, 
  AlertTriangle, 
  CheckCircle, 
  Zap,
  Calendar,
  MapPin
} from 'lucide-react';

const TransitDisplay = ({ transitData, onBack, isMobile }) => {
  const getIntensityColor = (intensity) => {
    switch (intensity) {
      case 'very high': return '#ff4757';
      case 'high': return '#ff6b35';
      case 'medium': return '#ffa502';
      case 'low': return '#2ed573';
      default: return '#747d8c';
    }
  };

  const getInfluenceIcon = (influence) => {
    switch (influence) {
      case 'Harmonious':
      case 'Expansive':
        return <CheckCircle size={16} color="#2ed573" />;
      case 'Challenging':
        return <AlertTriangle size={16} color="#ff4757" />;
      default:
        return <Star size={16} color="#ffa502" />;
    }
  };

  const getInfluenceColor = (influence) => {
    switch (influence) {
      case 'Harmonious':
      case 'Expansive':
        return '#2ed573';
      case 'Challenging':
        return '#ff4757';
      default:
        return '#ffa502';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Back Button */}
      <button
        onClick={onBack}
        style={{
          background: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem',
          fontSize: '0.9rem'
        }}
      >
        <ArrowLeft size={16} />
        Back to Form
      </button>

      {/* Current Time */}
      <div style={{
        background: 'rgba(0, 0, 0, 0.6)',
        borderRadius: '16px',
        padding: '1rem',
        border: '1px solid rgba(255, 215, 0, 0.3)',
        backdropFilter: 'blur(10px)',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <Clock size={20} color="#FFD700" />
        <span style={{ color: 'white', fontSize: '0.9rem' }}>
          Current Time: {new Date(transitData.currentTime).toLocaleString()}
        </span>
      </div>

      {/* Birth Info Summary */}
      <div style={{
        background: 'rgba(0, 0, 0, 0.6)',
        borderRadius: '16px',
        padding: '1rem',
        border: '1px solid rgba(255, 215, 0, 0.3)',
        backdropFilter: 'blur(10px)',
        marginBottom: '1rem'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.75rem',
          color: '#FFD700',
          fontSize: '0.9rem',
          fontWeight: '600'
        }}>
          <Calendar size={16} />
          <span>Birth Information</span>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '0.5rem',
          fontSize: '0.8rem',
          color: 'rgba(255, 255, 255, 0.8)'
        }}>
          <div>Date: {transitData.birthInfo.birthDate}</div>
          <div>Time: {transitData.birthInfo.birthTime}</div>
          <div>Location: {transitData.birthInfo.birthPlace}</div>
        </div>
      </div>

      {/* Major Transits */}
      {transitData.majorTransits && transitData.majorTransits.length > 0 && (
        <div style={{
          background: 'rgba(0, 0, 0, 0.6)',
          borderRadius: '16px',
          padding: '1rem',
          border: '1px solid rgba(255, 215, 0, 0.3)',
          backdropFilter: 'blur(10px)',
          marginBottom: '1rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem',
            color: '#FFD700',
            fontSize: '1rem',
            fontWeight: '600'
          }}>
            <Zap size={20} />
            <span>Major Transits</span>
          </div>
          {transitData.majorTransits.map((transit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '0.75rem',
                border: `1px solid ${getIntensityColor(transit.intensity)}20`
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '0.5rem'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    color: getIntensityColor(transit.intensity),
                    fontWeight: '600',
                    fontSize: '1rem'
                  }}>
                    {transit.planet}
                  </span>
                  <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    {transit.sign} {transit.degree}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  {getInfluenceIcon(transit.influence)}
                  <span style={{
                    color: getInfluenceColor(transit.influence),
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    {transit.influence}
                  </span>
                </div>
              </div>
              <div style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem',
                marginBottom: '0.5rem'
              }}>
                {transit.aspect} {transit.natalPlanet} ({transit.natalSign} {transit.natalDegree})
              </div>
              <div style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.85rem',
                lineHeight: '1.4'
              }}>
                {transit.description}
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '0.5rem',
                fontSize: '0.75rem',
                color: 'rgba(255, 255, 255, 0.6)'
              }}>
                <span>Orb: {transit.orb}</span>
                <span>Duration: {transit.duration}</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Current Transits */}
      <div style={{
        background: 'rgba(0, 0, 0, 0.6)',
        borderRadius: '16px',
        padding: '1rem',
        border: '1px solid rgba(255, 215, 0, 0.3)',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem',
          color: '#FFD700',
          fontSize: '1rem',
          fontWeight: '600'
        }}>
          <Star size={20} />
          <span>Current Transits</span>
        </div>
        {transitData.transits.map((transit, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '0.75rem',
              border: `1px solid ${getIntensityColor(transit.intensity)}20`
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '0.5rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{
                  color: getIntensityColor(transit.intensity),
                  fontWeight: '600',
                  fontSize: '1rem'
                }}>
                  {transit.planet}
                </span>
                <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {transit.sign} {transit.degree}
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                {getInfluenceIcon(transit.influence)}
                <span style={{
                  color: getInfluenceColor(transit.influence),
                  fontSize: '0.8rem',
                  fontWeight: '600'
                }}>
                  {transit.influence}
                </span>
              </div>
            </div>
            <div style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.9rem',
              marginBottom: '0.5rem'
            }}>
              {transit.aspect} {transit.natalPlanet} ({transit.natalSign} {transit.natalDegree})
            </div>
            <div style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.85rem',
              lineHeight: '1.4'
            }}>
              {transit.description}
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '0.5rem',
              fontSize: '0.75rem',
              color: 'rgba(255, 255, 255, 0.6)'
            }}>
              <span>Orb: {transit.orb}</span>
              <span>Duration: {transit.duration}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default TransitDisplay;
